/*! AAP Hero — Develop Device · All-Access Pass
 *  "Wave terrain": a perspective sound-wave landscape receding to a glowing
 *  horizon. Pure Canvas 2D — no three.js, no GSAP.
 *  Cursor-reactive: a light focus follows the pointer and the terrain swells
 *  toward it; moving the mouse pans the horizon. Bolder amplitudes + glow.
 *  Respects prefers-reduced-motion, pauses offscreen / when tab hidden,
 *  caps pixel ratio.
 *
 *  Markup contract (sections/all-access-pass.liquid):
 *    [data-aap-hero]    wrapper (gets .is-ready)
 *    [data-aap-canvas]  the <canvas> (position:absolute; inset:0)
 *  Optional: data-accent / data-accent-hi hex overrides on the wrapper.
 */
(function () {
  'use strict';

  var ACCENT    = '79,54,206';    // #4f36ce  rgb
  var ACCENT_HI = '128,60,238';   // #803cee  rgb
  var LINE      = '162,142,255';  // wave line tint (brighter)
  var BG_TOP    = '#0a0a12';
  var BG_BOT    = '#07070b';

  function ready(fn){
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }
  function hexToRgb(hex){
    var m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
    return m ? (parseInt(m[1],16)+','+parseInt(m[2],16)+','+parseInt(m[3],16)) : null;
  }

  function Terrain(root, canvas){
    this.root = root; this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.dpr = 1; this.w = 0; this.h = 0;
    this.t = 0; this.last = 0; this._raf = null;
    this.running = false; this.visible = true;
    // pointer: n* = normalised eased (-1..1); p* = eased pixel pos over hero
    this.pn = { x: 0, y: 0, tx: 0, ty: 0 };
    this.pp = { x: 0, y: 0, tx: 0, ty: 0, set: false };
    this.glow = 0; this.glowTarget = 0.0;  // focus intensity 0..1
    this.scroll = 0;
    this.reduce = !!(window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches);

    var a  = hexToRgb(root.getAttribute('data-accent'));
    var ah = hexToRgb(root.getAttribute('data-accent-hi'));
    if (a)  { ACCENT = a; }
    if (ah) { ACCENT_HI = ah; }

    this.ROWS = 30;
    // VCOLS (pocet svislic mrizky) se smazal spolu s nimi 13. 8. 2026.
    this.horizonY = 0.46;

    this._onResize  = this.resize.bind(this);
    this._onScroll  = this.onScroll.bind(this);
    this._onPointer = this.onPointer.bind(this);
    this._onLeave   = this.onLeave.bind(this);
    this._onVis     = this.onVisibility.bind(this);

    this.resize();
    window.addEventListener('resize', this._onResize, { passive: true });
    window.addEventListener('scroll', this._onScroll, { passive: true });
    if (!this.reduce) {
      window.addEventListener('pointermove', this._onPointer, { passive: true });
      document.addEventListener('pointerleave', this._onLeave, { passive: true });
    }
    document.addEventListener('visibilitychange', this._onVis);

    var self = this;
    if ('IntersectionObserver' in window){
      this.io = new IntersectionObserver(function(es){
        self.visible = es[0].isIntersecting;
        if (self.visible) self.kick(); else self.stop(true);
      }, { threshold: 0 });
      this.io.observe(root);
    }
  }

  Terrain.prototype.resize = function(){
    var r = this.root.getBoundingClientRect();
    var small = r.width < 760;
    this.dpr = Math.min(window.devicePixelRatio || 1, small ? 1.5 : 2);
    this.w = Math.max(1, r.width);
    this.h = Math.max(1, r.height);
    this.canvas.width  = Math.round(this.w * this.dpr);
    this.canvas.height = Math.round(this.h * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    if (!this.pp.set){
      this.pp.x = this.pp.tx = this.w * 0.5;
      this.pp.y = this.pp.ty = this.h * this.horizonY;
      this.pp.set = true;
    }
    if (!this.running || this.reduce) this.draw(true);
  };

  Terrain.prototype.onPointer = function(e){
    var r = this.root.getBoundingClientRect();
    var x = e.clientX - r.left, y = e.clientY - r.top;
    var inside = x >= -40 && x <= r.width + 40 && y >= -40 && y <= r.height + 40;
    this.pp.tx = x; this.pp.ty = y;
    this.pn.tx = (x / r.width - 0.5) * 2;
    this.pn.ty = (y / r.height - 0.5) * 2;
    this.glowTarget = inside ? 1 : 0.15;
    this.kick();
  };

  Terrain.prototype.onLeave = function(){ this.glowTarget = 0.12; };

  Terrain.prototype.onScroll = function(){
    var r = this.root.getBoundingClientRect();
    var h = r.height || 1;
    this.scroll = Math.min(Math.max(-r.top / h, 0), 1);
    if (this.reduce && this.running) this.draw(true);
  };

  Terrain.prototype.onVisibility = function(){
    if (document.hidden) this.stop(true);
    else this.kick();
  };

  Terrain.prototype.start = function(){
    this.running = true;
    this.root.classList.add('is-ready');
    if (this.reduce){ this.draw(true); return; }
    this.last = performance.now();
    this.kick();
  };

  Terrain.prototype.kick = function(){
    if (this._raf || this.reduce || !this.running || !this.visible || document.hidden) return;
    this.last = performance.now();
    this._raf = requestAnimationFrame(this.loop.bind(this));
  };

  Terrain.prototype.stop = function(soft){
    if (this._raf){ cancelAnimationFrame(this._raf); this._raf = null; }
    if (!soft) this.running = false;
  };

  Terrain.prototype.loop = function(now){
    this._raf = null;
    var dt = Math.min((now - this.last) / 1000, 0.05);
    this.last = now; this.t += dt;
    this.pn.x += (this.pn.tx - this.pn.x) * 0.06;
    this.pn.y += (this.pn.ty - this.pn.y) * 0.06;
    this.pp.x += (this.pp.tx - this.pp.x) * 0.10;
    this.pp.y += (this.pp.ty - this.pp.y) * 0.10;
    this.glow += (this.glowTarget - this.glow) * 0.05;
    this.draw(false);
    this.kick();
  };

  Terrain.prototype.draw = function(stat){
    var c = this.ctx, w = this.w, h = this.h, t = this.t;
    var s = this.scroll;
    var lift = s * h * 0.12;
    var fade = 1 - s * 0.85;
    var g = this.glow;

    // background wash
    var bg = c.createLinearGradient(0, 0, 0, h);
    bg.addColorStop(0, BG_TOP);
    bg.addColorStop(1, BG_BOT);
    c.fillStyle = bg;
    c.fillRect(0, 0, w, h);

    // pointer pans the horizon laterally + a touch vertically
    var cx = w * 0.5 + this.pn.x * w * 0.07;
    var horizon = h * this.horizonY - lift + this.pn.y * h * 0.035;
    var px = this.pp.x, py = this.pp.y;

    // ambient horizon glow (stronger)
    var rg = c.createRadialGradient(cx, horizon, 0, cx, horizon, w * 0.62);
    rg.addColorStop(0, 'rgba(' + ACCENT_HI + ',' + (0.42 * fade) + ')');
    rg.addColorStop(0.5, 'rgba(' + ACCENT + ',' + (0.12 * fade) + ')');
    rg.addColorStop(1, 'rgba(' + ACCENT + ',0)');
    c.fillStyle = rg;
    c.fillRect(0, 0, w, h);

    // ZADNA VODOROVNA CARA ANI SVISLA MRIZKA (rozhodnuti majitele 13. 8. 2026).
    // Do te doby se sem kreslily dve dalsi vrstvy: "bright horizon seam", tedy
    // ostra cara pres celou sirku na urovni horizontu, a "converging vertical
    // rails", tedy 15 svislic sbihajicich se k nemu. Zustava jen zare (radialni
    // gradient vyse) a samotne vlny nize.
    //
    // `horizon` se POCITA DAL: vlny z nej berou perspektivu (viz `y = horizon
    // + ease * ...`), takze to neni mrtva promenna. Odstranilo se kresleni,
    // ne geometrie.
    c.lineCap = 'round';

    // cursor-driven swell: terrain lifts in a band around the pointer x
    var spread = w * 0.16;
    var swellAmp = 46 * g;

    // receding horizontal wave lines (bolder)
    var phase = stat ? 0.15 : (t * 0.05);
    var rows = this.ROWS;
    var lines = [];
    for (var r = 0; r < rows; r++){
      lines.push(((r / rows) + (phase % 1)) % 1);
    }
    lines.sort(function(a, b){ return a - b; });

    for (var i = 0; i < lines.length; i++){
      var d = lines[i];
      var ease = d * d;
      var y = horizon + ease * (h - horizon + 30);
      var amp = (8 + ease * 78);
      var al = (0.07 + ease * 0.74) * fade;
      var wob = this.pn.x * ease * 34;
      c.beginPath();
      for (var x = 0; x <= w; x += 6){
        var p = (x - cx) / w;
        var wave =
            Math.sin(p * 6.6 + t * 0.6 + d * 9.0) * amp
          + Math.sin(p * 15.0 - t * 0.42 + d * 4.0) * amp * 0.32
          + Math.sin(p * 2.3 + t * 0.22) * amp * 0.28;
        // local swell toward the cursor
        if (swellAmp > 0.4){
          var dx = (x - px) / spread;
          wave += Math.exp(-dx * dx) * swellAmp * ease *
                  (0.7 + 0.3 * Math.sin(t * 2.2 + d * 6));
        }
        var yy = y - wave * 0.5;
        if (x === 0) c.moveTo(x + wob, yy); else c.lineTo(x + wob, yy);
      }
      var tint = ease > 0.55 ? ACCENT_HI : LINE;
      c.strokeStyle = 'rgba(' + tint + ',' + al + ')';
      c.lineWidth = ease * 2.0 + 0.45;
      c.stroke();
    }

    // luminous focus that follows the cursor
    if (g > 0.02){
      var pulse = 1 + 0.06 * Math.sin(t * 3);
      var fr = w * 0.24 * pulse;
      var fg = c.createRadialGradient(px, py, 0, px, py, fr);
      fg.addColorStop(0, 'rgba(' + ACCENT_HI + ',' + (0.40 * g * fade) + ')');
      fg.addColorStop(0.35, 'rgba(' + ACCENT_HI + ',' + (0.16 * g * fade) + ')');
      fg.addColorStop(1, 'rgba(' + ACCENT_HI + ',0)');
      c.save();
      c.globalCompositeOperation = 'lighter';
      c.fillStyle = fg;
      c.fillRect(0, 0, w, h);
      // a small bright core
      var cr = c.createRadialGradient(px, py, 0, px, py, 26);
      cr.addColorStop(0, 'rgba(210,195,255,' + (0.5 * g * fade) + ')');
      cr.addColorStop(1, 'rgba(210,195,255,0)');
      c.fillStyle = cr;
      c.fillRect(px - 40, py - 40, 80, 80);
      c.restore();
    }

    // vignette
    var vg = c.createRadialGradient(cx, h * 0.55, h * 0.2, cx, h * 0.55, w * 0.75);
    vg.addColorStop(0, 'rgba(7,7,11,0)');
    vg.addColorStop(1, 'rgba(7,7,11,0.55)');
    c.fillStyle = vg;
    c.fillRect(0, 0, w, h);
  };

  Terrain.prototype.destroy = function(){
    this.stop();
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('pointermove', this._onPointer);
    document.removeEventListener('pointerleave', this._onLeave);
    document.removeEventListener('visibilitychange', this._onVis);
    if (this.io) this.io.disconnect();
  };

  ready(function(){
    var root = document.querySelector('[data-aap-hero]');
    var canvas = root && root.querySelector('[data-aap-canvas]');
    if (!root || !canvas) return;
    var hero = new Terrain(root, canvas);
    hero.start();
    window.__aapHero = hero;
  });
})();
